/**
 * Kakao JavaScript SDK 동적 로더 + init 캐시.
 *
 * - Kakao 공유(Feed) 기능에는 "JavaScript 키" 필요 (REST API 키와 다름).
 * - 환경변수 VITE_KAKAO_JS_KEY 미설정 시 전체 기능 비활성화.
 */

declare global {
  interface Window {
    Kakao?: any;
  }
}

const SDK_URL = 'https://t1.kakaocdn.net/kakao_js_sdk/2.7.2/kakao.min.js';
const SDK_INTEGRITY =
  'sha384-TiCUE00h649CAMonG018J2ujOgDKW/kVWlChEuu4jK2vxfAAD0eZxzCKakxg55G4';

let loadPromise: Promise<any> | null = null;
let inited = false;

function resolveKey(): string | null {
  const key = import.meta.env.VITE_KAKAO_JS_KEY as string | undefined;
  if (!key || key.trim().length === 0) return null;
  return key.trim();
}

/** Kakao SDK 사용 가능 여부 (빌드 시점 키 존재 여부) */
export function isKakaoAvailable(): boolean {
  return !!resolveKey();
}

/** SDK 로드 + init. 실패 시 null 반환. 여러 번 호출해도 1회만 실제 로드됨. */
export async function loadKakao(): Promise<any | null> {
  const key = resolveKey();
  if (!key) return null;

  if (window.Kakao && inited) return window.Kakao;

  if (!loadPromise) {
    loadPromise = new Promise((resolve, reject) => {
      if (window.Kakao) return resolve(window.Kakao);
      const script = document.createElement('script');
      script.src = SDK_URL;
      script.integrity = SDK_INTEGRITY;
      script.crossOrigin = 'anonymous';
      script.async = true;
      script.onload = () => resolve(window.Kakao);
      script.onerror = () => reject(new Error('Kakao SDK 로드 실패'));
      document.head.appendChild(script);
    });
  }

  try {
    const Kakao = await loadPromise;
    if (!Kakao) return null;
    if (!Kakao.isInitialized?.()) {
      Kakao.init(key);
    }
    inited = true;
    return Kakao;
  } catch {
    return null;
  }
}
